import { html, render } from 'htm/preact';
import { useState, useEffect } from 'preact/hooks'

/**
 * @typedef RasterTimeFrame
 * @property {number} index
 * @property {string} id
 * @property {Date} datetime
 * @property {string} url
 */

/**
 * @typedef {RasterTimeFrame[]} RasterTimeSeries
 */

/**
 * @typedef {Object} TimelineControlOptions
 * @property {string[]} [aqiLayers]
 * @property {RasterTimeSeries} [smoke]
 * @property {string} [smokeError]
 */

export class TimelineControl {
    /**
     * @param {TimelineControlOptions} options
     */
    constructor(options) {
        /** @type {TimelineControlOptions} */
        this._options = options;
    }

    /**
     * @param {mapboxgl.Map} map
     */
    onAdd(map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
        this._container.style.width = `calc(100% - 20px)`;
        this._container.style.maxWidth = `360px`;

        render(html`<${Control} options=${this._options} map=${this._map} />`, this._container);

        return this._container;
    }

    /**
     * @param {TimelineControlOptions} options
     */
    update(options) {
        this._options = {...this._options, ...options};
        if (!this._container || !this._map) {
            return;
        }
        render(html`<${Control} options=${this._options} map=${this._map} />`, this._container);
    }

    onRemove() {
        // @ts-expect-error
        this._container.parentNode.removeChild(this._container);
        this._map = undefined;
    }
}

/**
 *
 * @param {{options: TimelineControlOptions, map: mapboxgl.Map}} param0
 * @returns
 */
function Control({options, map}) {
    const [view, setView] = useState('aqi');

    /**
     * @param {string} view
     */
    useEffect(() => {
        const smokeLayer = options.smoke?.[0];
        if (view === 'aqi') {
            if (smokeLayer && map.getLayer(smokeLayer.id)) {
                map.setPaintProperty(smokeLayer.id, 'raster-opacity', 0);
                map.setLayoutProperty(smokeLayer.id, 'visibility', 'none');
            }
            options.aqiLayers?.forEach((layer) => {
                if (map.getLayer(layer)) {
                    map.setLayoutProperty(layer, 'visibility', 'visible');
                }
            });
        } else {
            options.aqiLayers?.forEach((layer) => {
                if (map.getLayer(layer)) {
                    map.setLayoutProperty(layer, 'visibility', 'none');
                }
            });
            if (smokeLayer && map.getLayer(smokeLayer.id)) {
                map.setLayoutProperty(smokeLayer.id, 'visibility', 'visible');
            }
        }
    }, [view, options]);;

    return html`<div style="padding: 5px 10px 5px 5px">
        <${Toggle} onChange=${setView} />
        ${view ==="smoke" && options.smoke && html`<${Timeline} series=${options.smoke} map=${map} />`}
        ${view ==="smoke" && options.smokeError && html`<div style="padding-top: 5px; text-align: center">${options.smokeError}</div>`}
    </div>`;
}

/**
 *
 * @param {{onChange?: (value: string) => unknown}} param0
 * @returns
 */
function Toggle({onChange}) {
    const [view, setView] = useState('aqi');

    /**
     * @param {any} event
     */
    const handleChange = (event) => {
        setView(event.target.value);
        onChange && onChange(event.target.value);
    };

    return html`<div style="text-align: center">
        <label><input type="radio" name="view" value="aqi" checked=${view === 'aqi'} onChange=${handleChange} />Air Quality</label>
        <label><input type="radio" name="view" value="smoke" checked=${view === 'smoke'} onChange=${handleChange} />Smoke</label>
    </div>`;
}

const datetimeFormat = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric',
});

/**
 * @param {mapboxgl.Map} map
 * @param {RasterTimeFrame} frame
 */
function showFrame(map, frame) {
    const source = /** @type {mapboxgl.ImageSource} */(map.getSource(frame.id));
    source.updateImage({url: frame.url});
    map.setLayoutProperty(frame.id, 'visibility', 'visible');
    map.setPaintProperty(frame.id, 'raster-opacity', 0.75);
}

/**
 * @param {{series: RasterTimeSeries, map: mapboxgl.Map}} param0
 */
function Timeline({series, map}) {
    const [value, setValue] = useState(() => {
        const now = new Date();
        const closest = series.reduce((prev, curr) => {
            // @ts-expect-error
            return (Math.abs(curr.datetime - now) < Math.abs(prev.datetime - now) ? curr : prev);
        });
        showFrame(map, closest);
        return closest.index;
    });

    /**
     * @param {any} event
     */
    const handleChange = (event) => {
        const newValue = parseInt(event.target.value);
        setValue(() => {
            showFrame(map, series[newValue]);

            return newValue;
        });
    };

    return html`<div style="text-align: center">
        ${datetimeFormat.format(series[value].datetime)}<br />
        <input type="range" style="width: 100%" min="0" max=${series.length -1} value=${value} onInput=${handleChange} />
    </div>`;
}
